/* =========================================================================
   Penny Premium 3D — global.js
   One small module for the global chrome. Nothing here runs ahead of user
   interaction except the header scroll listener and the reveal observer.

   Exposes window.penny = { on, off, emit } — the event bus every later
   phase uses. Reserved events so far:
     'cart:updated'      detail: { item_count } — dispatched after any cart change
     'cart-drawer:open'  no detail — request to open the cart drawer (Phase 5)
   ========================================================================= */

/* --- Tiny pub/sub --------------------------------------------------------
   Thin wrapper over DOM CustomEvents on a private target, so any script
   (theme or app embed) can talk to the theme without touching internals. */
const bus = new EventTarget();

const penny = {
  on(event, handler) {
    bus.addEventListener(event, handler);
    return () => bus.removeEventListener(event, handler);
  },
  off(event, handler) {
    bus.removeEventListener(event, handler);
  },
  emit(event, detail) {
    bus.dispatchEvent(new CustomEvent(event, { detail }));
  },
};

window.penny = penny;

/* Bridge: plain DOM CustomEvents on document also reach the bus, so app
   embeds can `document.dispatchEvent(new CustomEvent('cart:updated', …))`. */
['cart:updated', 'cart-drawer:open'].forEach((name) => {
  document.addEventListener(name, (event) => penny.emit(name, event.detail));
});

/* --- Focus trap ----------------------------------------------------------
   trapFocus(container) keeps Tab cycling inside `container` until
   releaseFocus() is called. One trap active at a time (drawers are modal). */
const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

let activeTrap = null;

function trapFocus(container, initialFocus) {
  releaseFocus();

  const handler = (event) => {
    if (event.key !== 'Tab') return;
    const focusable = Array.from(container.querySelectorAll(FOCUSABLE)).filter(
      (el) => el.offsetParent !== null || el === document.activeElement
    );
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  activeTrap = { container, handler, returnTo: document.activeElement };
  container.addEventListener('keydown', handler);
  (initialFocus || container.querySelector(FOCUSABLE))?.focus();
}

function releaseFocus() {
  if (!activeTrap) return;
  activeTrap.container.removeEventListener('keydown', activeTrap.handler);
  if (activeTrap.returnTo instanceof HTMLElement) activeTrap.returnTo.focus();
  activeTrap = null;
}

penny.trapFocus = trapFocus;
penny.releaseFocus = releaseFocus;

/* --- Header scroll state -------------------------------------------------
   Adds .is-scrolled to the header wrapper after 24px, via rAF so the
   scroll handler itself does no layout work. */
function initHeaderScroll() {
  const header = document.querySelector('[data-header]');
  if (!header) return;

  let ticking = false;

  const update = () => {
    header.classList.toggle('is-scrolled', window.scrollY > 24);
    ticking = false;
  };

  window.addEventListener(
    'scroll',
    () => {
      if (!ticking) {
        ticking = true;
        window.requestAnimationFrame(update);
      }
    },
    { passive: true }
  );

  update();
}

/* --- Mobile menu drawer -------------------------------------------------- */
function initMenuDrawer() {
  const drawer = document.querySelector('[data-menu-drawer]');
  const openButton = document.querySelector('[data-menu-open]');
  if (!drawer || !openButton) return;

  const closeButtons = drawer.querySelectorAll('[data-menu-close]');
  const overlay = document.querySelector('[data-menu-overlay]');

  const open = () => {
    drawer.removeAttribute('hidden');
    overlay?.removeAttribute('hidden');
    // Next frame so the transition from the hidden state actually plays.
    requestAnimationFrame(() => {
      drawer.classList.add('is-open');
      overlay?.classList.add('is-open');
    });
    openButton.setAttribute('aria-expanded', 'true');
    document.body.classList.add('scroll-lock');
    trapFocus(drawer, drawer.querySelector('[data-menu-close]'));
    if (document.getElementById('MainContent')) document.getElementById('MainContent').inert = true;
  };

  const close = () => {
    drawer.classList.remove('is-open');
    overlay?.classList.remove('is-open');
    openButton.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('scroll-lock');
    releaseFocus();
    if (document.getElementById('MainContent')) document.getElementById('MainContent').inert = false;
    const done = () => {
      drawer.setAttribute('hidden', '');
      overlay?.setAttribute('hidden', '');
    };
    if (!document.documentElement.classList.contains('motion-full')) done();
    else drawer.addEventListener('transitionend', done, { once: true });
  };

  openButton.addEventListener('click', open);
  closeButtons.forEach((btn) => btn.addEventListener('click', close));
  overlay?.addEventListener('click', close);
  drawer.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') close();
  });
}

/* --- Desktop dropdown menus ----------------------------------------------
   Markup is <details data-dropdown><summary>…</summary><div>panel</div>.
   details/summary gives us click + keyboard toggling for free; this adds
   hover with a 120ms intent delay, Escape, arrow-key traversal, and
   close-on-outside-click. */
function initDropdowns() {
  const dropdowns = document.querySelectorAll('[data-dropdown]');
  if (dropdowns.length === 0) return;

  const closeAll = (except) => {
    dropdowns.forEach((d) => {
      if (d !== except) d.removeAttribute('open');
    });
  };

  dropdowns.forEach((dropdown) => {
    const summary = dropdown.querySelector('summary');
    let intentTimer = null;

    dropdown.addEventListener('mouseenter', () => {
      intentTimer = window.setTimeout(() => {
        closeAll(dropdown);
        dropdown.setAttribute('open', '');
      }, 120);
    });

    dropdown.addEventListener('mouseleave', () => {
      window.clearTimeout(intentTimer);
      intentTimer = window.setTimeout(() => dropdown.removeAttribute('open'), 120);
    });

    // Only one panel open at a time, whichever way it was opened.
    summary.addEventListener('click', () => closeAll(dropdown));

    dropdown.addEventListener('focusout', (event) => {
      if (!dropdown.contains(event.relatedTarget)) dropdown.removeAttribute('open');
    });

    dropdown.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        dropdown.removeAttribute('open');
        summary.focus();
        return;
      }
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
      event.preventDefault();
      if (!dropdown.hasAttribute('open')) dropdown.setAttribute('open', '');
      const links = Array.from(dropdown.querySelectorAll('a'));
      const index = links.indexOf(document.activeElement);
      const next =
        event.key === 'ArrowDown'
          ? links[Math.min(index + 1, links.length - 1)] || links[0]
          : links[Math.max(index - 1, 0)] || summary;
      next.focus();
    });
  });

  document.addEventListener('click', (event) => {
    if (![...dropdowns].some((d) => d.contains(event.target))) closeAll();
  });
}

/* --- Header search bar ---------------------------------------------------- */
function initHeaderSearch() {
  const toggle = document.querySelector('[data-search-toggle]');
  const panel = document.querySelector('[data-search-panel]');
  if (!toggle || !panel) return;

  const input = panel.querySelector('input[type="search"]');

  const setOpen = (open) => {
    panel.toggleAttribute('hidden', !open);
    toggle.setAttribute('aria-expanded', String(open));
    if (open) input?.focus();
    else toggle.focus();
  };

  toggle.addEventListener('click', () => setOpen(panel.hasAttribute('hidden')));
  panel.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') setOpen(false);
  });
  panel.querySelector('[data-search-close]')?.addEventListener('click', () => setOpen(false));
}

/* --- Cart count bubble + drawer hand-off ---------------------------------
   The bubble re-renders whenever any script emits 'cart:updated' with
   { item_count }. The cart icon dispatches 'cart-drawer:open'; until the
   drawer ships in Phase 5 the icon's href (/cart) is the fallback, so we
   only preventDefault once a drawer actually subscribes. */
function initCartLink() {
  const bubbles = document.querySelectorAll('[data-cart-count]');

  penny.on('cart:updated', (event) => {
    const count = event.detail?.item_count ?? 0;
    bubbles.forEach((bubble) => {
      const changed = bubble.textContent !== String(count);
      bubble.textContent = count > 99 ? '99+' : String(count);
      bubble.toggleAttribute('hidden', count === 0);
      if (changed && count > 0) {
        bubble.classList.add('pulse');
        bubble.addEventListener('animationend', () => bubble.classList.remove('pulse'), { once: true });
      }
    });
  });

  document.querySelectorAll('[data-cart-link]').forEach((link) => {
    link.addEventListener('click', (event) => {
      if (document.querySelector('[data-cart-drawer]')) {
        event.preventDefault();
        penny.emit('cart-drawer:open');
      }
    });
  });
}

/* --- Announcement bar dismiss -------------------------------------------- */
function initAnnouncementBar() {
  const bar = document.querySelector('[data-announcement]');
  if (!bar) return;

  const key = `penny:announcement-dismissed:${bar.dataset.announcement}`;

  try {
    if (sessionStorage.getItem(key)) {
      bar.setAttribute('hidden', '');
      return;
    }
  } catch (_) {
    /* storage unavailable (private mode) — just keep the bar visible */
  }

  bar.querySelector('[data-announcement-pause]')?.addEventListener('click', (event) => {
    const paused = bar.classList.toggle('is-paused');
    event.currentTarget.setAttribute('aria-pressed', String(paused));
  });

  bar.querySelector('[data-announcement-dismiss]')?.addEventListener('click', () => {
    bar.setAttribute('hidden', '');
    try {
      sessionStorage.setItem(key, '1');
    } catch (_) {
      /* ignore */
    }
  });
}

/* --- Reveal on scroll -----------------------------------------------------
   One shared IntersectionObserver for every .reveal element. Siblings
   inside the same parent stagger by 60ms via --reveal-index (set in CSS
   transition-delay). Disabled under reduced motion or when the merchant
   turns animations off — base.css then never hides the elements. */
function initRevealObserver() {
  // The motion gate (motion.js) is the single authority
  if (!document.documentElement.classList.contains('motion-full')) return;

  const elements = document.querySelectorAll('.reveal');
  if (elements.length === 0) return;

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-revealed');
        observer.unobserve(entry.target);
      });
    },
    { rootMargin: '0px 0px -10% 0px' }
  );

  elements.forEach((element) => {
    const siblings = element.parentElement
      ? Array.from(element.parentElement.children).filter((c) => c.classList.contains('reveal'))
      : [element];
    // Stagger caps at 5 — longer chains feel slow, the rest arrive together
    element.style.setProperty('--reveal-index', String(Math.min(siblings.indexOf(element), 4)));
    observer.observe(element);
  });
}

/* --- Honeypot guard -------------------------------------------------------
   Blocks submission when the hidden decoy field was filled by a bot.
   Covers the newsletter forms and any form marked [data-hp-guard]
   (e.g. the contact page). */
function initNewsletterGuard() {
  document.addEventListener('submit', (event) => {
    const form = event.target.closest('[data-newsletter-form], [data-hp-guard]');
    if (form?.querySelector('[name="newsletter-extra"]')?.value) event.preventDefault();
  });
}

/* --- Share row (product + article pages) ----------------------------------
   Delegated: [data-share-copy] copies the URL with "Copied" feedback,
   [data-share-native] uses the OS share sheet (revealed only when
   navigator.share exists). */
function initShare() {
  if (navigator.share) {
    document.querySelectorAll('[data-share-native]').forEach((b) => b.removeAttribute('hidden'));
  }

  document.addEventListener('click', async (event) => {
    const copy = event.target.closest('[data-share-copy]');
    if (copy) {
      try {
        await navigator.clipboard.writeText(window.location.href);
        const label = copy.querySelector('span:last-child');
        const idle = label.textContent;
        label.textContent = copy.dataset.copiedLabel;
        window.setTimeout(() => (label.textContent = idle), 2000);
      } catch (_) {
        /* clipboard unavailable — the URL bar still exists */
      }
      return;
    }
    if (event.target.closest('[data-share-native]')) {
      navigator.share?.({ title: document.title, url: window.location.href }).catch(() => {});
    }
  });
}

/* --- Localization selectors ----------------------------------------------
   Country/language selects in the footer submit their form on change. */
function initLocalizationForms() {
  document.querySelectorAll('[data-localization-form] select').forEach((select) => {
    select.addEventListener('change', () => select.form?.submit());
  });
}

/* --- Boot ----------------------------------------------------------------- */
initHeaderScroll();
initMenuDrawer();
initDropdowns();
initHeaderSearch();
initCartLink();
initAnnouncementBar();
initRevealObserver();
initNewsletterGuard();
initShare();
initLocalizationForms();
